import {isFeatureEnabled} from 'feature-flags';
import {Form, FormButton, FormField, FormSubmitHandler} from 'platform/components';
import {Box, getSize, Grid, GridItem, HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {css} from 'styled-components';
import {array, object, string} from 'yup';

import {useRef} from 'react';

import {isNotNil} from 'ramda-adjunct';

import {
  CommonConditionAttribute,
  CommonDescriptionAttribute,
  CommonDigitalCertificateAttribute,
  CommonDigitalCertificateThemeAttribute,
  CommonGenerationAttribute,
  CommonTitleExtensionAttribute,
  CommonVersionAttribute,
  CommonVideoUrlAttribute,
  useGetSalesPlatformsQuery,
  VehicleGeneralAdvertisingSettingsResponseBody,
  GetSalesPlatformsApiRes,
} from '@dms/api/sales';
import {useGetDescriptionGeneratorConfigQuery} from '@dms/api/settings';
import {AdvertisingPlatformCodeEnum} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {AiVehicleDescriptionBanner, PredefinedNotes, usePermissions} from '@dms/shared';

import {DOT_CHARACTER, parseDate, suffixTestId, TestIdProps} from 'shared/web';

import {DescriptionGeneratorInputs} from '../types/DescriptionGeneratorInputs';
import {GeneralSettingsFormValues} from '../types/GeneralSettingsFormValues';
import {getAttributeByCode} from '../utils/getAttributeByCode';
import {BelgianLanguageVariantsCard} from './BelgianLanguageVariantsCard';
import {ChangeDigitalCertificateThemeDynamically} from './ChangeDigitalCertificateThemeDynamically';
import {DescriptionFormField} from './DescriptionFormField';
import {DescriptionGenerator} from './DescriptionGenerator';

const CONDITION_OPTIONS: {label: string; value: string}[] = [
  {label: i18n.t('page.advertisement.labels.condition.excellent'), value: '1'},
  {label: i18n.t('page.advertisement.labels.condition.veryGood'), value: '2'},
  {label: i18n.t('page.advertisement.labels.condition.good'), value: '3'},
  {label: i18n.t('page.advertisement.labels.condition.acceptable'), value: '4'},
  {label: i18n.t('page.advertisement.labels.condition.serviceable'), value: '5'},
];

export interface GeneralSettingsFormProps extends TestIdProps {
  vehicleId: string;
  descriptionLabel: string;
  data: VehicleGeneralAdvertisingSettingsResponseBody | undefined;
  versionOptions: Array<{value: string; label: string}>;
  generationOptions: Array<{value: string; label: string}>;
  digitalCertificateOptions: Array<{value: string; label: string}>;
  digitalCertificateThemeOptions: Array<{value: string; label: string}>;
  isDisabled: boolean;
  isMultilanguageActive?: boolean;
  enabledLanguages?: Array<{value: string; label: string}>;
  platforms?: GetSalesPlatformsApiRes;
  initialLocalizedContent?: Record<
    string,
    {titleExtension: string | null; description: string | null}
  >;
  onSubmit: FormSubmitHandler<GeneralSettingsFormValues>;
  onHandleEdit: () => void;
}

export function GeneralSettingsForm(props: GeneralSettingsFormProps) {
  const formatDateTime = useDateTimeFormatter();
  const {data: platforms} = useGetSalesPlatformsQuery({vehicleId: props.vehicleId});
  const [canGenerateDescription] = usePermissions({
    permissionKeys: ['vehicleSalesGenerateDescription'],
  });
  const {data: descriptionGeneratorConfig} = useGetDescriptionGeneratorConfigQuery(
    {},
    {
      skip:
        !isFeatureEnabled(featureFlags.SETTINGS_ADVERTISEMENT_DESCRIPTION_GENERATOR) ||
        !canGenerateDescription,
    }
  );
  const isOtomotoEnabled = platforms?.find(
    ({platform}) => platform.code === AdvertisingPlatformCodeEnum.OTOMOTO
  )?.platform.enabled;

  const digitalCertificateTheme = getAttributeByCode<CommonDigitalCertificateThemeAttribute>(
    'digital_certificate_theme',
    props.data?.attributes
  )?.value;

  const conditionFormValue = getAttributeByCode<CommonConditionAttribute>(
    'condition',
    props.data?.attributes
  )?.value?.toString();
  const defaultValues: GeneralSettingsFormValues = {
    description:
      getAttributeByCode<CommonDescriptionAttribute>('description', props.data?.attributes)
        ?.value ?? null,
    title:
      getAttributeByCode<CommonTitleExtensionAttribute>('title_extension', props.data?.attributes)
        ?.value ?? null,
    version:
      getAttributeByCode<CommonVersionAttribute>('version', props.data?.attributes)?.value ?? null,
    generation:
      getAttributeByCode<CommonGenerationAttribute>('generation', props.data?.attributes)?.value ??
      null,
    digitalCertificate:
      getAttributeByCode<CommonDigitalCertificateAttribute>(
        'digital_certificate',
        props.data?.attributes
      )?.value ?? null,
    digitalCertificateTheme: digitalCertificateTheme ?? null,
    videoUrl:
      getAttributeByCode<CommonVideoUrlAttribute>('video_url', props.data?.attributes)?.value ??
      null,
    condition: isNotNil(conditionFormValue) ? [conditionFormValue] : null,
    localizedContent: props.initialLocalizedContent,
  };

  const descriptionGeneratorInputsRef = useRef<DescriptionGeneratorInputs | null>(null);

  return (
    <Form<GeneralSettingsFormValues>
      values={defaultValues}
      resetOptions={{keepDirtyValues: true}}
      schema={schema}
      onSubmit={props.onSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi) => (
        <Box position="relative">
          <VStack spacing={4}>
            <div
              // Styling hack, but we need PredefinedNotes inside the form and positioned next to tab headers
              css={css`
                position: absolute;
                top: 0;
                right: 0;
                transform: translateY(calc(-100% - ${getSize(4)}));
              `}
            >
              <PredefinedNotes
                note={formApi.watch('description')}
                onPrefill={(description) =>
                  formApi.setValue('description', description, {shouldDirty: true})
                }
                resource="VEHICLE"
                context="advertisement"
                data-testid={suffixTestId('predefinedNotes', props)}
              />
            </div>
            <Show
              whenFeatureEnabled={featureFlags.SETTINGS_ADVERTISEMENT_DESCRIPTION_GENERATOR}
              when={canGenerateDescription && !!descriptionGeneratorConfig?.enabled}
            >
              <AiVehicleDescriptionBanner />
              <DescriptionGenerator
                vehicleId={props.vehicleId}
                enabledLanguages={props.isMultilanguageActive ? props.enabledLanguages : undefined}
                inputsRef={descriptionGeneratorInputsRef}
                onGenerate={(content) => {
                  formApi.setValue('description', content, {shouldDirty: true});
                }}
                onGenerateLocalized={(results) => {
                  results.forEach(({language, content}) => {
                    formApi.setValue(`localizedContent.${language}.description`, content, {
                      shouldDirty: true,
                    });
                  });
                }}
              />
            </Show>
            <Show when={!props.isMultilanguageActive}>
              <DescriptionFormField
                vehicleId={props.vehicleId}
                control={control}
                descriptionLabel={props.descriptionLabel}
                isDisabled={props.isDisabled}
                data-testid={props['data-testid']}
              />
            </Show>
            {props.isMultilanguageActive && props.enabledLanguages && (
              <BelgianLanguageVariantsCard
                control={control}
                enabledLanguages={props.enabledLanguages}
                vehicleId={props.vehicleId}
                isDisabled={props.isDisabled}
                platforms={props.platforms}
                formApi={formApi}
                descriptionGeneratorInputsRef={descriptionGeneratorInputsRef}
              />
            )}
            <Grid columns={[1, 2, 4]}>
              <GridItem span={[1, 2]}>
                <FormField
                  control={control}
                  label={i18n.t('page.advertisement.labels.titleExtension')}
                  name="title"
                  type="text"
                  isDisabled={props.isDisabled}
                  tooltip={i18n.t('page.advertisement.labels.titleExtensionTooltip')}
                  maxLength={30}
                  isCounterVisible
                  data-testid={suffixTestId('title', props)}
                />
              </GridItem>
              <Show
                when={isOtomotoEnabled}
                whenFeatureEnabled={featureFlags.ADVERTISEMENT_VERSION_GENERATION_ATTRIBUTES}
              >
                <FormField
                  control={control}
                  label={i18n.t('page.advertisement.labels.version')}
                  tooltip={i18n.t('page.advertisement.labels.versionTooltip')}
                  name="version"
                  type="choice"
                  options={props.versionOptions}
                  isDisabled={props.isDisabled}
                  data-testid={suffixTestId('version', props)}
                />
              </Show>
              <Show
                when={isOtomotoEnabled}
                whenFeatureEnabled={featureFlags.ADVERTISEMENT_VERSION_GENERATION_ATTRIBUTES}
              >
                <FormField
                  control={control}
                  label={i18n.t('page.advertisement.labels.generation')}
                  tooltip={i18n.t('page.advertisement.labels.generationTooltip')}
                  name="generation"
                  type="choice"
                  options={props.generationOptions}
                  isDisabled={props.isDisabled}
                  data-testid={suffixTestId('generation', props)}
                />
              </Show>
              <Show
                whenFeatureEnabled={
                  featureFlags.ADVERTISEMENT_DIGITAL_CERTIFICATE_VIDEO_URL_ATTRIBUTES
                }
              >
                <FormField
                  control={control}
                  label={i18n.t('page.advertisement.labels.digitalCertificate')}
                  tooltip={i18n.t('page.advertisement.labels.digitalCertificateTooltip')}
                  name="digitalCertificate"
                  type="choice"
                  options={props.digitalCertificateOptions}
                  isDisabled={props.isDisabled}
                  data-testid={suffixTestId('digitalCertificate', props)}
                />
                <ChangeDigitalCertificateThemeDynamically
                  formApi={formApi}
                  passedDigitalCertificateTheme={digitalCertificateTheme}
                />
                <FormField
                  control={control}
                  label={i18n.t('page.advertisement.labels.digitalCertificateTheme')}
                  tooltip={i18n.t('page.advertisement.labels.digitalCertificateThemeTooltip')}
                  name="digitalCertificateTheme"
                  type="choice"
                  options={props.digitalCertificateThemeOptions}
                  isDisabled={props.isDisabled}
                  data-testid={suffixTestId('digitalCertificateTheme', props)}
                />
                <FormField
                  control={control}
                  label={i18n.t('page.advertisement.labels.videoUrl')}
                  tooltip={i18n.t('page.advertisement.labels.videoUrlTooltip')}
                  name="videoUrl"
                  type="text"
                  isDisabled={props.isDisabled}
                  data-testid={suffixTestId('videoUrl', props)}
                />
              </Show>
            </Grid>
            <HStack>
              <FormField
                control={control}
                type="chips"
                options={CONDITION_OPTIONS}
                name="condition"
                isDisabled={props.isDisabled}
                label={i18n.t('page.advertisement.labels.conditionTitle')}
                tooltip={i18n.t('page.advertisement.labels.conditionTooltip')}
                data-testid={suffixTestId('condition', props)}
                isDeselectable
              />
            </HStack>
            {props.data?.updatedAt && (
              <Show when={isNotNil(props.data?.updatedBy)}>
                <Text size="xSmall" color="secondary">
                  {`${i18n.t(`page.advertisement.labels.lastUpdateBy`)} ${
                    props.data?.updatedBy
                  } ${DOT_CHARACTER} ${formatDateTime(
                    'dateTimeShort',
                    parseDate(props.data?.updatedAt)
                  )}`}
                </Text>
              </Show>
            )}
            <HStack justify="space-between">
              <Link
                leftIcon="image/edit"
                title={i18n.t('page.advertisement.actions.editParameters')}
                size="small"
                onClick={props.onHandleEdit}
                data-testid="button-advertisement.listingAttributes.edit"
              />
              <Show when={!props.isDisabled}>
                <FormButton
                  type="submit"
                  control={control}
                  title={i18n.t('general.actions.confirm')}
                  data-testid={props['data-testid']}
                />
              </Show>
            </HStack>
          </VStack>
        </Box>
      )}
    </Form>
  );
}

const schema = object().shape({
  title: string().nullable(),
  description: string().nullable(),
  videoUrl: string().url().nullable(),
  condition: array(string()).nullable(),
});
